import { Injectable } from '@angular/core';
import { ArticleDto, LieuStockageDto, PartenaireDto, PartenaireType } from '@lexi-clients/lexi';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { IGridService } from '../../models/igrid-service';
import { ArticleListService } from '../../services/article.service';
import { CustomStoreService } from '../../services/custom-store.service';
import { LieuStockageListService } from '../../services/lieu-stockage.service';
import { MouvementMarchandiseListService } from '../../services/mouvement-marchandise.service';
import { ParcListService } from '../../services/parc-list.service';
import { PartenaireListService } from '../../services/partenaire.service';
import { DossierListService } from '../../services/dossier-list.service';

@Injectable()
export class DxDataSourceService {
  constructor(
    private readonly partenaireService: PartenaireListService,
    private readonly dossierService: DossierListService,
    private readonly lieuStockageService: LieuStockageListService,
    private readonly mouvementMarchandiseListService: MouvementMarchandiseListService,
    private readonly articleService: ArticleListService,
    private readonly parcService : ParcListService
  ) {}

  //#region Méthodes génériques
  /* Le nom du paramètre doit correspondre au nom du paramètre côté serveur */
  private  getAdditionalParams(params: Record<string, any>): Map<string, any> {
    const additionalParams = new Map<string, any>();
    for (const key in params) {
      if (params[key] != null) additionalParams.set(key, params[key]);
    }
    return additionalParams;
  }

  getDataSourceForSelectBox(
    gridService: IGridService<any>,
    additionalParams: Map<string, string> = null,
    key: string = 'id',
    sortSelector: string = null,
    manipulateDataOnLoad?: (data: any) => void
  ): DataSource {
    const paramDataSource: any = {
      key,
      paginate: true,
      pageSize: 10,
      store: new CustomStoreService(gridService).getSelectBoxCustomStore(additionalParams, manipulateDataOnLoad)
    };

    // Ordonne les résultats via sortSelector (ex: intitule, libelle, etc...)
    if (sortSelector) {
      paramDataSource.sort = [
        { selector: sortSelector, desc: false }
      ];
    }

    return new DataSource(paramDataSource);
  }

  getCustomStore(gridService: IGridService<any>, additionalParams: Map<string, string> = null, customFilters: string[] = [], manipulateDataOnLoad?: (data: any) => void, customStoreOptionsToOverride = {}): CustomStore {
    return new CustomStoreService(gridService).getCustomStore(additionalParams, customFilters, customStoreOptionsToOverride, manipulateDataOnLoad)
  }

  getTreeListCustomStore(gridService: IGridService<any>, additionalParams: Map<string, string> = null) {
    return new CustomStoreService(gridService).getTreeListCustomStore(additionalParams);
  }
  //#endregion

  //#region Partenaires
  getPartenaireDataSourceForSelectBox(estClient?: boolean, estFournisseur?: boolean, partenaireType?: PartenaireType, avecPieceASolder?: boolean, filtrerParSitesAutorises?: boolean): DataSource {
    return this.getDataSourceForSelectBox(
      this.partenaireService,
      this.getAdditionalParams({estClient, estFournisseur, partenaireType, avecPieceASolder, filtrerParSitesAutorises})
    );
  }

  getPartenaireCustomStore(estClient?: boolean, estFournisseur?: boolean, partenaireType?: PartenaireType, pattern: string = null, classificationTypeId: number = null, classificationValueIds: string = null, sansClassification: boolean = false, manipulateDataOnLoad?: (data: any) => void): CustomStore {
    return new CustomStoreService(this.partenaireService).getCustomStore(
      this.getAdditionalParams({estClient, estFournisseur, partenaireType, pattern, classificationTypeId, classificationValueIds, sansClassification}),
      null,
      null,
      manipulateDataOnLoad
    );
  }

  async getPartenaireById(id: number): Promise<PartenaireDto> {
    const partenaireDataSource = this.getPartenaireDataSourceForSelectBox();
    partenaireDataSource.filter(["id", "=", id]);
    const partenaire: PartenaireDto = (await partenaireDataSource.load())?.[0];
    return partenaire;
  }

  async getPartenaireByCodeBo(codeBo: string): Promise<PartenaireDto> {
    const partenaireDataSource = this.getPartenaireDataSourceForSelectBox();
    partenaireDataSource.filter(["codeBo", "=", codeBo]);
    const partenaire: PartenaireDto = (await partenaireDataSource.load())?.[0];
    return partenaire;
  }
  //#endregion

  //#region Dossiers
  getDossierDataSourceForSelectBox(): DataSource {
    return this.getDataSourceForSelectBox(
      this.dossierService,
    );
  }
  //#endregion

  //#region Lieux de stockage
  getLieuStockageDataSourceForSelectBox(siteId?: number, currentSiteOnly?: boolean, currentSitePartenaireOnly?: boolean, partenaireId?: number) {
    return this.getDataSourceForSelectBox(
      this.lieuStockageService,
      this.getAdditionalParams({siteId, currentSiteOnly, currentSitePartenaireOnly, partenaireId})
    );
  }

  async getLieuStockageByCodeBo(codeBo: string): Promise<LieuStockageDto> {
    const lieuStockageDataSource = this.getLieuStockageDataSourceForSelectBox();
    lieuStockageDataSource.filter(["codeBo", "=", codeBo]);
    const lieuStockage: LieuStockageDto = (await lieuStockageDataSource.load())?.[0];
    return lieuStockage;
  }
  //#endregion

  //#region Mouvements
  getMouvementsCustomStore(partenaireId?: number, articleId?: number, bonId?: number, documentLogistiqueCodeBo?: string, noPiece?: string, manipulateDataOnLoad?: (data: any) => void): CustomStore {
    return new CustomStoreService(this.mouvementMarchandiseListService).getCustomStore(
      this.getAdditionalParams({partenaireId, articleId, bonId, documentLogistiqueCodeBo, noPiece}),
      null,
      null,
      manipulateDataOnLoad
    );
  }
  //#endregion

  //#region Articles
  async getArticleById(id: number, avecVariante: boolean): Promise<ArticleDto> {
    const additionalParams = new Map();
    if (avecVariante) additionalParams.set("avecVariante", true);
    const articleDataSource = this.getDataSourceForSelectBox(this.articleService, additionalParams);
    articleDataSource.filter(["id", "=", id]);
    const article: ArticleDto = (await articleDataSource.load())?.[0];
    return article;
  }
  //#endregion

  //#region Parcs
  getParcsDataSourceForSelectBox(clientId? : number){
    return this.getDataSourceForSelectBox(this.parcService,this.getAdditionalParams({clientId}));
  }
  //#endregion
}